import React, { useEffect, useState } from "react";
import { Box, Button, Grid, Stack, Tooltip, Typography } from "@mui/material";
import KeyIcon from "@mui/icons-material/Key";
import RefreshIcon from "@mui/icons-material/Refresh";
import { COLORS } from "./constants";
import { barePanelSx, ghostButtonSx, primaryButtonSx, sectionSubtleSx } from "./styles";
import { CopyableField } from "./CopyableField";

// RTMP settings inside the Source card. The stream key is hidden by default.
//
// "Generate Token" used to be the most prominent button on the page even while
// live. /token/generate RETURNS THE CURRENT KEY while it is still valid (keys
// last one hour) and only mints a new one after that, so the button is a
// primary "Get stream key" only before there is a key, and a quiet "Get a fresh
// key" afterwards.
// The key is a credential: the page opens knowing only WHETHER one is valid
// and until when (`keyMasked`, from /token/status). Showing it needs a passkey
// check in the last 5 minutes, which "Show key" triggers when due.
//
// The key only has to be valid when the encoder CONNECTS (checked once, at
// publish), so the line under the fields is about starting, not about a live
// stream being cut off: one that is already running keeps going past expiry.
function useMinuteClock(active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 20_000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

export function RtmpIngestPanel({
  rtmpUrl, rtmpToken, displayUserId, generateStreamToken, copiedField, copyToClipboard,
  keyExpiresAt, keyMasked
}) {
  const hasKey = Boolean(rtmpUrl && rtmpToken);
  const now = useMinuteClock((hasKey || !!keyMasked) && !!keyExpiresAt);
  const minsLeft = keyExpiresAt ? Math.floor((keyExpiresAt - now) / 60000) : null;
  const expired = keyExpiresAt != null && keyExpiresAt <= now;
  const until = keyExpiresAt
    ? new Date(keyExpiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <Box sx={barePanelSx}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}
        alignItems={{ sm: "center" }} justifyContent="space-between">
        <Box>
          <Typography sx={{ color: COLORS.text, fontWeight: 600, fontSize: 15 }}>Encoder settings</Typography>
          <Typography sx={sectionSubtleSx}>
            {hasKey
              ? "In OBS: Settings → Stream → Service: Custom. Paste both values."
              : keyMasked && !expired
                ? `A key (${keyMasked}) is valid until ${until}. Show it to copy it into your encoder.`
                : "Get a private key to connect OBS, vMix or any RTMP encoder."}
          </Typography>
        </Box>
        {hasKey ? (
          <Tooltip title="Keys last one hour. This shows your current key if it's still valid, otherwise a new one.">
            <Button startIcon={<RefreshIcon />} onClick={() => generateStreamToken()}
              sx={{ ...ghostButtonSx, flexShrink: 0, alignSelf: { xs: "flex-start", sm: "center" } }}>
              Get a fresh key
            </Button>
          </Tooltip>
        ) : (
          <Button startIcon={<KeyIcon />} variant="contained" onClick={() => generateStreamToken()}
            sx={{ ...primaryButtonSx, flexShrink: 0, alignSelf: { xs: "flex-start", sm: "center" } }}>
            {keyMasked && !expired ? "Show key" : "Get stream key"}
          </Button>
        )}
      </Stack>

      {hasKey && (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <CopyableField
              label="Server" value={rtmpUrl} fieldId="rtmpUrl"
              copiedField={copiedField} onCopy={copyToClipboard}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CopyableField
              label="Stream key" value={`${displayUserId}?sign=${rtmpToken}`} fieldId="rtmpToken"
              copiedField={copiedField} onCopy={copyToClipboard} secret
            />
          </Grid>
          {keyExpiresAt && (
            <Grid item xs={12}>
              <Typography role="status" sx={{
                display: "flex", alignItems: "center", gap: 1, fontSize: 13,
                color: expired ? COLORS.amber : minsLeft < 10 ? COLORS.amber : COLORS.muted
              }}>
                <Box component="span" aria-hidden sx={{ width: 7, height: 7, borderRadius: "50%", flex: "none",
                  bgcolor: expired || minsLeft < 10 ? COLORS.amber : COLORS.green }} />
                {expired
                  ? `This key expired at ${until}. Get a fresh key before you start streaming.`
                  : `Key valid for ${minsLeft < 1 ? "less than a minute" : `${minsLeft} more minute${minsLeft === 1 ? "" : "s"}`}: start streaming before ${until}. A stream that's already live keeps going.`}
              </Typography>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
}
